import type { QueryClient } from '@tanstack/react-query';
import type { AppDispatch } from './store';
import {
  authApi,
  dashboardApi,
  integrationsApi,
  eventLogsApi,
  supportTicketsApi,
  usersApi,
  clientsApi,
  profileApi,
  settingsApi,
} from '../api/baseApis';
import projectApi from '../api/projectsApi';

/**
 * Drop every cached server response so the whole app re-reads from the API.
 *
 * The app fetches through two clients, and both have to be cleared or the UI ends up half
 * refreshed: RTK Query backs the modules below, while React Query backs the rest (projects,
 * source config, subscriptions, dashboard widgets, …). React Query is passed in rather than
 * imported, because the client lives in the provider module, which imports the store.
 *
 * `invalidateQueries` is deliberate: it refetches what is on screen and marks everything else
 * stale, rather than emptying the cache and flashing skeletons across pages nobody is looking at.
 *
 * Awaiting the returned promise waits for the on-screen refetches to land.
 */
export const clearApiCaches = async (dispatch: AppDispatch, queryClient: QueryClient): Promise<void> => {
  dispatch(authApi.util.resetApiState());
  dispatch(dashboardApi.util.resetApiState());
  dispatch(integrationsApi.util.resetApiState());
  dispatch(eventLogsApi.util.resetApiState());
  dispatch(supportTicketsApi.util.resetApiState());
  dispatch(usersApi.util.resetApiState());
  dispatch(clientsApi.util.resetApiState());
  dispatch(profileApi.util.resetApiState());
  dispatch(settingsApi.util.resetApiState());
  dispatch(projectApi.util.resetApiState());

  await queryClient.invalidateQueries();
};
